import { useTheme } from "@emotion/react";
import React, { useMemo } from "react";
import { MdBlock, MdCheckCircleOutline, MdOutlineAccessTime, MdOutlineInsertDriveFile } from "react-icons/md";

import { ticketStatuses, ticketStatusLabels, TicketStatus } from "@megaron/support-contracts";

import { SupportSelect, SupportSelectOption } from "./SupportSelect";

type Props = {
  value: TicketStatus;
  isDisabled?: boolean;
  onChange: (status: TicketStatus) => void;
};

const statusColor = (
  status: TicketStatus,
  colors: { primary: string; success: string; danger: string; warning: string },
) => {
  if (status === "resolved") return colors.success;
  if (status === "closed") return colors.danger;
  if (status === "pending") return colors.warning;
  return colors.primary;
};

const statusIcon = (status: TicketStatus) => {
  if (status === "resolved") return <MdCheckCircleOutline size={18} />;
  if (status === "closed") return <MdBlock size={18} />;
  if (status === "pending") return <MdOutlineAccessTime size={18} />;
  return <MdOutlineInsertDriveFile size={18} />;
};

export const SupportStatusSelect: React.FC<Props> = ({ value, onChange, isDisabled }) => {
  const theme = useTheme();

  const options = useMemo<SupportSelectOption<TicketStatus>[]>(
    () =>
      ticketStatuses.map((status) => ({
        value: status,
        label: ticketStatusLabels[status],
        color: statusColor(status, theme.colors),
        icon: statusIcon(status),
      })),
    [theme.colors],
  );

  return (
    <SupportSelect<TicketStatus>
      value={value}
      options={options}
      onChange={(next) => next && onChange(next)}
      isDisabled={isDisabled}
    />
  );
};
